import React, { useEffect, useCallback, useState} from 'react';
import {legato} from 'pattern/utils/legato.js';
import PianoChopper from './PianoChopper.js';
import PianoStrummer from './PianoStrummer.js';
import PianoExpander from './PianoExpander.js';
import PianoExponentialExpander from './PianoExponentialExpander.js';
import PianoSpreader from './PianoSpreader.js';
import UtilityPopup from "components/pianoroll2/UtilityPopup.js";
import Colors from 'components/Colors.js';
import {WIDTH} from 'components/pianoroll/constants.js';
import {onMouseUp} from 'components/pianoroll/onMouseUp.js';
import {
    mouseDown,
    setStepToCopy,
    setIsDraggingDuration,
    setStepDataDragging,
    setTransposeDragging,
    setSelectedPianoRollSteps} from 'actions/index.js';
import {connect} from 'react-redux';

const colors = new Colors();


// UTILITY MODES:
const CHOPPING = "CHOPPING";
const STRUMMING = "STRUMMING";
const EXPANDING = "EXPANDING";
const SPREADING = "SPREADING";
const EXPONENTIAL_EXPANDING = "EXPONENTIAL_EXPANDING";

const MODES = [
    {value: CHOPPING, label: "Chop Notes"},
    {value: STRUMMING, label: "Strum Notes"},
    {value: EXPANDING, label: "Expand Notes"},
    {value: EXPONENTIAL_EXPANDING, label: "Exponential Expand Notes"},
    {value: SPREADING, label: "Spread Notes"},
];

const PianoRollContextDropdown = ({
    steps,
    xScale,
    mouseDown,
    setStepToCopy,
    setIsDraggingDuration,
    setStepDataDragging,
    setTransposeDragging,
    setSelectedSteps,
    selectedSteps=[],
    track,
    dropdownPos,
    clear
}) => {
    const [utilityMode, setUtilityMode] = useState(undefined);

    const _clear = useCallback(() => {
        console.log("CLEAR CALLED");
        onMouseUp({
            mouseDown,
            setStepToCopy,
            setIsDraggingDuration,
            setStepDataDragging,
            setTransposeDragging
        });
        setUtilityMode(undefined);
        clear();
    }, [clear, setUtilityMode]);

    useEffect(() => {
        window.addEventListener("click", _clear);
        return () => window.removeEventListener("click", _clear);
    }, []);

    useEffect(() => {
        setUtilityMode(undefined);
    }, [dropdownPos]);

    const slideNotes = useCallback((e) => {
        selectedSteps.forEach(s => s.slide=!s.slide);
        setSelectedSteps(undefined);
        _clear();
    }, [selectedSteps, track, setSelectedSteps, clear, setUtilityMode]);

    if (!dropdownPos) { return []; };

    let style = {
        top: dropdownPos.y + 28,
        left: dropdownPos.x,
    };
    let length = steps ? steps.length : 1;
    let width = length * xScale * WIDTH;
    let numSteps = selectedSteps.length;
  
    let inner = "";
    if (!utilityMode) {
        inner = [
         <div className="menu-header">
            <div style={{backgroundColor: colors.getColorForTrack(track)}}
                 className="dot"/>
            <div className="header-text">
              {numSteps > 0 ? numSteps : "No"} {numSteps === 1 ? "step" : "steps"} selected
            </div>
          </div>,
            <div
              onClick={(e) => e.stopPropagation()}
              className="menu-items">
            <div
              onClick={slideNotes}
              className="menu-item">
              {selectedSteps.every(s => s.slide) ? "Unslide Notes" : "Slide Notes"}
              <img src="/slider-icon.svg" className="utility-icon"/>
            </div>
            <div onClick={() => setUtilityMode(CHOPPING)} className="menu-item">
              Chop Notes
              <img src="/chopper-icon.svg" className="utility-icon"/>
            </div>
            <div onClick={() => setUtilityMode(STRUMMING)} className="menu-item">
              Strum Notes 
              <img src="/strummer-icon.svg" className="utility-icon"/>
            </div>
            <div onClick={() => setUtilityMode(EXPANDING)} className="menu-item">
              Expand Notes 
              <img src="/expander-icon.svg" className="utility-icon"/>
            </div>
            <div onClick={() => setUtilityMode(EXPONENTIAL_EXPANDING)} className="menu-item">
              Expanding Notes 
              <img src="/expander-icon.svg" className="utility-icon"/>
            </div>
            <div onClick={() => setUtilityMode(SPREADING)} className="menu-item">
              Spread Notes 
              <img src="/spreader-icon.svg" className="utility-icon"/>
            </div>
            <div
              onClick={() => legato(track, selectedSteps)}
              className="menu-item">
              Legato Notes
              <img src="/legato-icon.svg" className="utility-icon"/>
            </div>
          </div>
        ];
    } else {
        let utilityInner = '';
        let name = "";
        let className = "";
        if (utilityMode === CHOPPING) {
            name = "Chopper";
            className = "chopper";
            utilityInner = (<PianoChopper clear={_clear} track={track} selectedSteps={selectedSteps}/>);
        } else if (utilityMode === STRUMMING) {
            name = "Strummer";
            utilityInner = (<PianoStrummer clear={_clear} track={track} selectedSteps={selectedSteps}/>);
        } else if (utilityMode == EXPANDING) {
            name = "Expander";
            utilityInner = (<PianoExpander clear={_clear} track={track} selectedSteps={selectedSteps}/>);
        } else if (utilityMode == EXPONENTIAL_EXPANDING) {
            name = "Exponential Expander";
            utilityInner = (<PianoExponentialExpander clear={_clear} track={track} selectedSteps={selectedSteps}/>);
        } else if (utilityMode == SPREADING) {
            name = "Spreader";
            utilityInner = (<PianoSpreader clear={_clear} track={track} selectedSteps={selectedSteps}/>);
        }

        inner = (
            <UtilityPopup
              modes={MODES}
              setMode={setUtilityMode}
              mode={utilityMode}
              name={name} className={className} clear={_clear}>
              {utilityInner}
            </UtilityPopup>
        );
    }
    
    return (
        <div style={style} className="piano-context-menu">
          {inner}
       </div>
    );
};

const mapStateToProps = (state, props) => {
    let steps = state.tracks.trackSteps[props.track.id];
    return {
        steps,
        selectedSteps: state.keyboard.selectedPianoRollSteps
    };
};

const mapDispatchToProps = (dispatch) => {
  return {
      mouseDown: (x) => dispatch(mouseDown(x)),
      setStepToCopy: (x) => dispatch(setStepToCopy(x)),
      setIsDraggingDuration: (x) => dispatch(setIsDraggingDuration(x)),
      setStepDataDragging: (x) => dispatch(setStepDataDragging(x)),
      setTransposeDragging: (x) => dispatch(setTransposeDragging(x)),
      setSelectedSteps: (steps) => dispatch(setSelectedPianoRollSteps(steps)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PianoRollContextDropdown);
